import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Input } from "formik-semantic-ui";
import { Divider, Header, Message, Segment } from "semantic-ui-react";

import { validateStampAssignment } from "utilities/dist/stamp/validator";
import { parseServerErrorResponse } from "utilities/dist/api";
import { assignStamps } from "./actions";
import { StampRollSelectorComponent } from "../roll/selector.component";
import { formatStampRange } from "../../common/helpers";
import { showSummaryToast } from "./helpers";

export const StampAssignmentDestroyContainer = () => {
    const dispatch = useDispatch();
    const [assignmentErrors, setAssignmentErrors] = useState([]);
    const { isLoadingRolls, rolls } = useSelector(({ stampRoll }) => ({
        rolls: stampRoll.data
            .filter(({ active, finished }) => active && !finished)
            .map(roll => ({ ...roll, firstAvailable: roll.from, lastAvailable: roll.to })),
        isLoadingRolls: stampRoll.isLoading,
    }));

    const handleSubmit = async (stampData, formik) => {
        try {
            const validatedStampData = await validateStampAssignment(stampData);

            const response = await dispatch(assignStamps(validatedStampData));
            formik.setSubmitting(false);
            if (response) {
                formik.resetForm();
                showSummaryToast(response, validatedStampData);
            }
        } catch (err) {
            const messages = [];

            if (err.details) {
                err.details.forEach(errDet => {
                    formik.setFieldError(errDet.context.key, errDet.message);
                    messages.push(errDet.message);
                });
            } else {
                messages.push(...parseServerErrorResponse(err));
            }

            setAssignmentErrors(messages);
            formik.setSubmitting(false);
        }
    };
    return (
        <>
            <Header
                subheader="Manually destroy stamps if they are no longer usable"
                content="Destroy stamps"
                as="h3"
            />
            <Segment vertical loading={isLoadingRolls}>
                <Form
                    onSubmit={handleSubmit}
                    initialValues={{
                        roll_id: null,
                        note: "",
                        from: 0,
                        to: 0,
                    }}
                    render={({ values, setFieldValue }) => {
                        const selectedRollIndex = rolls?.findIndex(
                            ({ id }) => id === values.roll_id
                        );
                        const selectedRoll = rolls[selectedRollIndex];
                        return (
                            <Form.Children>
                                <StampRollSelectorComponent
                                    rolls={rolls}
                                    canAddNewRoll={false}
                                    dropdownProps={{
                                        text: selectedRoll
                                            ? formatStampRange(selectedRoll)
                                            : "Select Roll",
                                        value: selectedRollIndex || null,
                                        "data-cy": "roll_selector",
                                    }}
                                    onSelect={rollIndex => {
                                        const roll = rolls[rollIndex];
                                        if (!roll) return;
                                        setFieldValue("roll_id", roll.id);
                                        setFieldValue("from", roll.from);
                                        setFieldValue("to", roll.from + 1);
                                    }}
                                />
                                {values?.roll_id && (
                                    <>
                                        <Divider hidden />
                                        <Form.Group widths="2">
                                            <Input
                                                name="from"
                                                label="Start"
                                                inputProps={{
                                                    type: "number",
                                                    min: selectedRoll?.from || 0,
                                                    max: selectedRoll?.to || 1,
                                                    "data-cy": "stamp_from",
                                                }}
                                            />

                                            <Input
                                                name="to"
                                                label="End"
                                                inputProps={{
                                                    type: "number",
                                                    min: selectedRoll?.from || 0,
                                                    max: selectedRoll?.to || 1,
                                                    "data-cy": "stamp_to",
                                                }}
                                            />
                                        </Form.Group>

                                        <Input
                                            name="note"
                                            label="Note"
                                            inputProps={{
                                                "data-cy": "note",
                                            }}
                                        />

                                        <Button.Submit data-cy="save_roll" positive>
                                            Save Roll
                                        </Button.Submit>
                                    </>
                                )}
                            </Form.Children>
                        );
                    }}
                />

                {assignmentErrors.length > 0 && (
                    <Message
                        error
                        icon="warning sign"
                        content={assignmentErrors}
                        header="Error saving stamp!"
                    />
                )}
            </Segment>
        </>
    );
};
